import { useEffect, useMemo, useRef } from 'react';

import { useClickOutside } from '../../hooks/use-click-outside';
import type { SlashCommandItem } from '../../hooks/use-slash-commands';

interface Props {
  /** Full list of available commands. Filtering happens inside the picker. */
  commands: SlashCommandItem[];
  status: 'idle' | 'loading' | 'success' | 'error';
  /** Current filter (the text after `/`). */
  filter: string;
  selectedIndex: number;
  onHover: (index: number) => void;
  onSelect: (cmd: SlashCommandItem) => void;
  onClose: () => void;
  onRefresh?: (() => void) | undefined;
}

export function SlashCommandPicker({
  commands,
  status,
  filter,
  selectedIndex,
  onHover,
  onSelect,
  onClose,
  onRefresh,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  useClickOutside(ref, onClose);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q),
    );
  }, [commands, filter]);

  const selectedRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  return (
    <div
      ref={ref}
      role="listbox"
      aria-label="Slash commands"
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 6px)',
        left: 0,
        right: 0,
        maxHeight: 280,
        background: 'var(--surface-raise)',
        border: '1px solid var(--border-strong)',
        borderRadius: 10,
        boxShadow: 'var(--shadow-soft)',
        overflow: 'hidden',
        zIndex: 40,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-alt)',
          fontSize: 10,
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontWeight: 600,
        }}
      >
        <span style={{ color: 'var(--accent)' }}>/</span>
        <span>Commands</span>
        <span style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
          <kbd style={kbdStyle}>↑↓</kbd> navigate <kbd style={kbdStyle}>↵</kbd> select <kbd style={kbdStyle}>esc</kbd> close
        </span>
      </div>

      <div style={{ overflowY: 'auto', maxHeight: 240, padding: 4 }}>
        {status === 'loading' && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>Loading commands…</div>
        )}
        {status === 'error' && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--kind-failure)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Failed to load commands.</span>
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                style={{ fontSize: 11, color: 'var(--accent)', background: 'transparent', border: 0, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}
              >
                Retry
              </button>
            )}
          </div>
        )}
        {(status === 'success' || status === 'idle') && visible.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)', textAlign: 'center' }}>
            {filter ? 'No matching commands' : 'No commands available'}
          </div>
        )}
        {visible.map((cmd, i) => {
          const isSelected = i === selectedIndex;
          return (
            <div
              key={cmd.name}
              ref={isSelected ? selectedRef : undefined}
              role="option"
              aria-selected={isSelected}
              onMouseEnter={() => onHover(i)}
              onMouseDown={(e) => {
                // Use mousedown so the input doesn't lose focus + the
                // selection fires before our click-outside handler on document.
                e.preventDefault();
                onSelect(cmd);
              }}
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                padding: '7px 12px 7px 16px',
                borderRadius: 6,
                cursor: 'pointer',
                background: isSelected ? 'var(--accent-soft)' : 'transparent',
              }}
            >
              {isSelected && (
                <span
                  style={{
                    position: 'absolute',
                    left: 6,
                    top: 8,
                    bottom: 8,
                    width: 2,
                    borderRadius: 2,
                    background: 'var(--accent)',
                  }}
                />
              )}
              <div
                style={{
                  fontSize: 13,
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  fontWeight: 600,
                  color: 'var(--text)',
                  letterSpacing: '-0.01em',
                }}
              >
                <span style={{ color: 'var(--accent)', opacity: 0.75 }}>/</span>
                {cmd.name}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-soft)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {cmd.description}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const kbdStyle = {
  padding: '1px 5px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 3,
  fontSize: 10,
  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
  color: 'var(--text-soft)',
} satisfies React.CSSProperties;

export function filterCommands(commands: SlashCommandItem[], filter: string): SlashCommandItem[] {
  const q = filter.trim().toLowerCase();
  if (!q) return commands;
  return commands.filter(
    (c) => c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q),
  );
}
